import { PointArray } from '@editor/interfaces/attribute'
import { InspectionArea } from '@editor/interfaces/inspection'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { Mode, Steps, Tools, ViewSides } from './definitions'

export interface PCDAlignmentState {
  /**
   * Inspection area for the left side.
   */
  leftInspectionArea: InspectionArea | null

  /**
   * Progress percentage of the left side PCD download.
   * Null if not downloading.
   */
  leftProgress: number | null

  /**
   * Progress percentage of the right side PCD download.
   * Null if not downloading.
   */
  rightProgress: number | null

  /**
   * Current side being interacted.
   */
  currentSide: ViewSides

  /**
   * Anchors for the left side.
   */
  leftAnchors: PointArray[]

  /**
   * Anchors for the right side.
   */
  rightAnchors: PointArray[]

  /**
   * Anchor placement history.
   * This is used to undo anchor placements.
   */
  anchorHistory: ViewSides[]

  /**
   * Current mode.
   */
  currentMode: Mode

  /**
   * Current tool.
   */
  currentTool: Tools

  /**
   * Current step of the alignment process.
   */
  currentStep: Steps

  /**
   * The area ID of the area that is currently being hovered.
   */
  currentHoveringArea: string | null

  /**
   * Transform matrix to align left PCD to right PCD.
   */
  transformMatrix: number[] | null

  /**
   * Loading state.
   */
  isLoading: boolean

  /**
   * Canvas width and height.
   */
  canvasRect: { x: number; y: number; width: number; height: number }

  /**
   * Area IDs that are connected to the current area.
   */
  linkedAreas: string[]
}

export const initialState: PCDAlignmentState = {
  leftInspectionArea: null,
  leftProgress: null,
  rightProgress: null,
  currentSide: ViewSides.Left,
  leftAnchors: [],
  rightAnchors: [],
  anchorHistory: [],
  currentMode: Mode.Normal,
  currentTool: Tools.Overview,
  currentStep: Steps.SelectPoints,
  currentHoveringArea: null,
  transformMatrix: null,
  isLoading: true,
  canvasRect: { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight },
  linkedAreas: [],
}

export const slice = createSlice({
  name: 'pcd-alignment',
  initialState,
  reducers: {
    setLeftInspectionArea: (state, action: PayloadAction<InspectionArea | null>) => {
      state.leftInspectionArea = action.payload
    },
    setLeftProgress: (state, action: PayloadAction<number | null>) => {
      state.leftProgress = action.payload
    },
    setRightProgress: (state, action: PayloadAction<number | null>) => {
      state.rightProgress = action.payload
    },
    setCurrentSide: (state, action: PayloadAction<ViewSides>) => {
      if (state.currentSide === action.payload) return
      state.currentSide = action.payload
    },
    setCurrentMode: (state, action: PayloadAction<Mode>) => {
      state.currentMode = action.payload
    },
    setCurrentTool: (state, action: PayloadAction<Tools>) => {
      state.currentTool = action.payload
    },
    setCurrentStep: (state, action: PayloadAction<Steps>) => {
      state.currentStep = action.payload
    },
    setCurrentHoveringArea: (state, action: PayloadAction<string | null>) => {
      state.currentHoveringArea = action.payload
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setTransformMatrix: (state, action: PayloadAction<number[] | null>) => {
      state.transformMatrix = action.payload
    },
    setCanvasRect: (state, action: PayloadAction<{ x: number; y: number; width: number; height: number }>) => {
      state.canvasRect = action.payload
    },
    setLinkedAreas: (state, action: PayloadAction<string[]>) => {
      state.linkedAreas = action.payload
    },
    addLeftAnchor: (state, action: PayloadAction<PointArray>) => {
      state.leftAnchors.push(action.payload)
      state.anchorHistory.push(ViewSides.Left)
    },
    setLeftAnchors: (state, action: PayloadAction<PointArray[]>) => {
      state.leftAnchors = action.payload
    },
    addRightAnchor: (state, action: PayloadAction<PointArray>) => {
      state.rightAnchors.push(action.payload)
      state.anchorHistory.push(ViewSides.Right)
    },
    setRightAnchors: (state, action: PayloadAction<PointArray[]>) => {
      state.rightAnchors = action.payload
    },
    undoAnchor: (state) => {
      if (state.anchorHistory.length === 0) return
      const lastSide = state.anchorHistory.pop()
      if (lastSide === ViewSides.Left) {
        state.leftAnchors = state.leftAnchors.slice(0, -1)
      } else if (lastSide === ViewSides.Right) {
        state.rightAnchors = state.rightAnchors.slice(0, -1)
      }

      state.currentStep = Steps.SelectPoints
    },
    clearAnchors: (state) => {
      state.leftAnchors = []
      state.rightAnchors = []
      state.anchorHistory = []
    },
    resetLeftInspectionArea: (state) => {
      state.leftInspectionArea = null
    },
    resetAlignmentTool: (state) => {
      state.leftProgress = null
      state.rightProgress = null
      state.leftAnchors = []
      state.rightAnchors = []
      state.anchorHistory = []
      state.currentStep = Steps.SelectPoints
      state.transformMatrix = null
    },
    reset: () => initialState,
  },
})

export const {
  setLeftInspectionArea,
  setCanvasRect,

  setIsLoading,
  setLeftProgress,
  setRightProgress,

  setCurrentMode,
  setCurrentTool,
  setCurrentSide,
  setCurrentStep,
  setCurrentHoveringArea,

  setTransformMatrix,
  setLinkedAreas,

  addLeftAnchor,
  addRightAnchor,
  setLeftAnchors,
  setRightAnchors,
  undoAnchor,
  clearAnchors,

  reset,
  resetAlignmentTool,
  resetLeftInspectionArea,
} = slice.actions
export default slice.reducer

export const LOCAL_FLAGS = {
  ignoreLeftCheck: false,
}
